<template>
  <ModalStep
    :id="'program-criterias-modal-content'"
    :header="{
      title: modalRole === 'mentee' ? t('dashboard.mentee.programCriteriasModal.title') : t('dashboard.mentor.programCriteriasModal.title'),
    }"
  >
    <template #bodyInformation>
      {{ modalRole === 'mentee' ? t('dashboard.mentee.programCriteriasModal.bodyInformation') : t('dashboard.mentor.programCriteriasModal.bodyInformation') }}
    </template>
    <template #bodyContent>
      <MatchingInformationContainer />
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton usage="button" type="button" color="white" @emit:click="closeModal" :text="t('ui.button.cancel')">
        </CustomButton>
        <CustomButton
          usage="button"
          type="button"
          color="primary"
          data-test-id="submit-program-criterias"
          @emit:click="onSubmit"
          :isLoading="isAddMatchCriteriaPendingRef"
          :text="t('ui.button.confirm')"
        ></CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useMutation } from '@tanstack/vue-query'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { mentorMatchFillCriteriaMutation } from '@/features/matching/api'
import MatchingInformationContainer from '@/features/matching/components/matching-modals/matching-modal-content/matching-information-container/matching-information-container.vue'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { invalidateUser } from '@/utils/lib/vue-query'

const { t } = useI18n()
const toast = useToast()
const { accountRef } = useAccount()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const modalRole = computed(() => modalsStore.modals['program-criterias'].attachedData?.role || 'mentor')

const { onSubmit } = useMatchingInformation({
  onSubmitCallback,
})

function closeModal() {
  modalsStore.toggleModal('program-criterias', false)
}

const { mutate: addMatchCriteria, isPending: isAddMatchCriteriaPendingRef } = useMutation({
  mutationFn: mentorMatchFillCriteriaMutation,
  onError: (error) => {
    console.error(error)
    toast?.error(t('error.common'))
  },
  onSuccess: async () => {
    await invalidateUser(accountRef.value!.id!)
    const successKey =
      modalRole.value === 'mentee'
        ? 'dashboard.mentee.programCriteriasModal.success'
        : 'dashboard.mentor.programCriteriasModal.success'
    toast?.success(t(successKey))
    closeModal()
  },
})

async function onSubmitCallback(filledCriteria: { inputId: string; value: string }[]) {
  await addMatchCriteria({
    programId: selectedProgramIdRef.value!,
    criteria: filledCriteria,
  })
}
</script>

<style lang="scss">
@import './program-criterias-modal-content.scss';
</style>
